import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import backendApiService from "../service/backend-api.service";
import { useAccount } from "../account/AccountContext";
import { FAVORITES_FULL, FAVORITES_STORAGE_KEY, MAX_FAVORITES, keyOf } from "./favoriteKey";

const SAVE_FAILED = "Не получилось сохранить избранное, попробуйте ещё раз";
const NOTICE_MS = 4000;

function readLocal() {
  try {
    const list = JSON.parse(window.localStorage.getItem(FAVORITES_STORAGE_KEY) || "[]");
    return Array.isArray(list) ? list.filter((item) => item?.platform && item?.gameKey) : [];
  } catch {
    return [];
  }
}

function writeLocal(list) {
  try {
    if (list.length) {
      window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(list));
    } else {
      window.localStorage.removeItem(FAVORITES_STORAGE_KEY);
    }
  } catch {
    /* приватный режим или переполнено — избранное живёт до перезагрузки */
  }
}

const noop = () => {};

const FavoritesContext = createContext({
  items: [],
  ready: false,
  synced: false,
  isFavorite: () => false,
  toggle: noop,
  save: noop,
  remove: noop,
});

/**
 * Без входа избранное лежит только в localStorage. После входа источник — кабинет:
 * избранное браузера один раз переносится туда и очищается. Если API кабинета
 * недоступно (старый бэкенд), остаёмся на localStorage.
 */
export function FavoritesProvider({ children }) {
  const { account, loading: accountLoading } = useAccount();
  const email = account?.email || null;
  const [items, setItems] = useState(readLocal);
  const [synced, setSynced] = useState(false);
  const [ready, setReady] = useState(false);
  const [notice, setNotice] = useState("");
  const itemsRef = useRef(items);
  const syncedRef = useRef(false);

  const apply = useCallback((next) => {
    itemsRef.current = next;
    setItems(next);
  }, []);

  const markSynced = useCallback((value) => {
    syncedRef.current = value;
    setSynced(value);
  }, []);

  useEffect(() => {
    if (accountLoading) return undefined;
    let alive = true;
    if (!email) {
      markSynced(false);
      apply(readLocal());
      setReady(true);
      return undefined;
    }
    setReady(false);
    const local = readLocal();
    (local.length ? backendApiService.mergeFavorites(local) : backendApiService.getFavorites())
      .then((list) => {
        if (!alive) return;
        if (local.length) writeLocal([]);
        apply(list);
        markSynced(true);
      })
      .catch(() => {
        if (!alive) return;
        apply(local);
        markSynced(false);
      })
      .finally(() => alive && setReady(true));
    return () => {
      alive = false;
    };
  }, [email, accountLoading, apply, markSynced]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === FAVORITES_STORAGE_KEY && !syncedRef.current) apply(readLocal());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [apply]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const commit = useCallback(
    async (next, request) => {
      const prev = itemsRef.current;
      apply(next);
      if (!syncedRef.current) {
        writeLocal(next);
        return true;
      }
      try {
        const saved = await request();
        if (saved?.gameKey) {
          apply(itemsRef.current.map((item) => (keyOf(item) === keyOf(saved) ? saved : item)));
        }
        return true;
      } catch (error) {
        apply(prev);
        setNotice(error.status === 409 ? FAVORITES_FULL : SAVE_FAILED);
        return false;
      }
    },
    [apply],
  );

  /** Добавить или обновить (регион, цену); новая игра — в начало списка. */
  const save = useCallback(
    (item) => {
      if (!item?.gameKey) return Promise.resolve(false);
      const key = keyOf(item);
      const current = itemsRef.current;
      const existing = current.find((other) => keyOf(other) === key);
      if (!existing && current.length >= MAX_FAVORITES) {
        setNotice(FAVORITES_FULL);
        return Promise.resolve(false);
      }
      const now = new Date().toISOString();
      const entry = {
        ...item,
        addedAt: existing?.addedAt || now,
        ...(item.price != null ? { priceAt: now } : {}),
      };
      const next = existing
        ? current.map((other) => (keyOf(other) === key ? entry : other))
        : [entry, ...current];
      return commit(next, () => backendApiService.saveFavorite(entry));
    },
    [commit],
  );

  const remove = useCallback(
    (item) => {
      const key = keyOf(item);
      const next = itemsRef.current.filter((other) => keyOf(other) !== key);
      return commit(next, () => backendApiService.removeFavorite(item.platform, item.gameKey));
    },
    [commit],
  );

  const isFavorite = useCallback(
    (item) => Boolean(item?.gameKey) && items.some((other) => keyOf(other) === keyOf(item)),
    [items],
  );

  const toggle = useCallback(
    (item) => (isFavorite(item) ? remove(item) : save(item)),
    [isFavorite, remove, save],
  );

  const value = useMemo(
    () => ({ items, ready, synced, isFavorite, toggle, save, remove }),
    [items, ready, synced, isFavorite, toggle, save, remove],
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
      {notice ? (
        <div role="status" className="fav-toast">
          {notice}
        </div>
      ) : null}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  return useContext(FavoritesContext);
}
